import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { BookOpen, Plus, FileText, Clock, Sparkle } from '@phosphor-icons/react';
import AiDocImportDialog from './AiDocImportDialog';
import { formatDate } from '@/utils/datetime';

export default function KnowledgeHome() {
  const navigate = useNavigate();
  const { flat, canEdit, newPage } = useOutletContext();
  const [aiDocOpen, setAiDocOpen] = useState(false);

  const recent = [...(flat || [])]
    .sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))
    .slice(0, 9);

  if (!flat || flat.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="kb-home-empty">
        <BookOpen size={44} weight="duotone" className="text-blue-400" />
        <h2 className="mt-4 text-lg font-semibold text-zinc-800">まだページがありません</h2>
        <p className="mt-1 text-sm text-zinc-400">社内の手順書・マニュアル・議事録などをまとめましょう。</p>
        {canEdit && (
          <button type="button" onClick={() => newPage(null)} data-testid="kb-home-new"
            className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            <Plus size={15} weight="bold" />最初のページを作成
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-10" data-testid="kb-home">
      <div className="flex items-center gap-2 text-zinc-800">
        <BookOpen size={22} weight="duotone" className="text-blue-600" />
        <h1 className="text-2xl font-bold tracking-tight">ナレッジ</h1>
        {canEdit && (
          <button type="button" onClick={() => setAiDocOpen(true)} data-testid="kb-ai-doc-import-btn"
            className="ml-auto inline-flex min-h-[32px] items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100">
            <Sparkle size={13} weight="fill" />AIドキュメント取り込み
          </button>
        )}
      </div>
      <AiDocImportDialog open={aiDocOpen} onClose={() => setAiDocOpen(false)} />
      <p className="mt-1 text-sm text-zinc-500">左のツリーからページを選ぶか、上部の検索で探せます。</p>

      <h2 className="mt-8 mb-3 flex items-center gap-1.5 text-sm font-semibold text-zinc-500"><Clock size={14} />最近更新したページ</h2>
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {recent.map((p) => (
          <button key={p.id} type="button" onClick={() => navigate(`/knowledge/p/${p.slug || p.id}`)} data-testid={`kb-recent-${p.id}`}
            className="group rounded-xl border border-zinc-200 bg-white p-4 text-left hover:border-blue-300 hover:shadow-sm transition-all">
            <div className="flex items-center gap-2">
              <span className="text-lg">{p.icon || <FileText size={18} className="text-zinc-400" />}</span>
              {p.status === 'draft' && <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">下書き</span>}
            </div>
            <div className="mt-2 truncate font-medium text-zinc-800 group-hover:text-blue-700" title={p.title || '無題のページ'}>{p.title || '無題のページ'}</div>
            {p.updated_at && <div className="mt-1 text-xs text-zinc-400">{formatDate(p.updated_at)}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}
